import { Box, Typography } from "@mui/material";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../Themes";

const ApplicationLogo = ({
    href = "/",
    enableHoveringEffect,
    maxWidth = 180,
    minWidth = 48,
    width = "100%",
    height = "100%",
    animate,
    overrideDefaultLogo,
    additionalTextLines = [],
    sx = {}
}) => {
    const { config } = useMicrospecDynamicUIContext();
    const { application } = config;
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { logo } = application;
    const usedLogo = overrideDefaultLogo || logo;

    // Animation style for logo
    const logoAnimation = animate
        ? {
            animation: 'logoSmoothAnim 2s infinite cubic-bezier(0.4, 0, 0.2, 1)'
        }
        : {};

    return (
        <Box
            component="a"
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"center"}
            href={href}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
                alignSelf: 'center',
                justifyContent: 'center',
                justifyItems: 'center',
                width: width,
                maxWidth: maxWidth,
                minWidth: minWidth,
                height: height,
                borderRadius: 2,
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: (theme) => enableHoveringEffect ? (theme.palette.mode === 'dark'
                        ? '0 12px 40px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                        : '0 12px 40px rgba(102, 126, 234, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.5)') : 'none'
                },
                ...sx,
            }}
        >
            {/* Add keyframes for smooth logo animation */}
            <style>
                {`
                    @keyframes logoSmoothAnim {
                        0% { transform: scale(1); }
                        10% { transform: scale(1.075); }
                        20% { transform: scale(1); }
                        30% { transform: scale(1.05); }
                        40% { transform: scale(1); }
                        100% { transform: scale(1); }
                    }
                `}
            </style>
            {application.logo ? (
                <Box
                    component="img"
                    src={usedLogo}
                    alt="Application Logo"
                    sx={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        filter: (theme) => theme.palette.mode === 'dark'
                            ? 'brightness(1.1) contrast(1.1)'
                            : 'none',
                        transition: 'all 0.3s ease',
                        ...logoAnimation,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: (theme) => theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        ...logoAnimation,
                    }}
                >
                    {localizationProvider.translateByType({
                        type: 'Localized',
                        text: 'application.name'
                    })}
                </Box>
            )}
            {/* Additional Text Lines */}
            {Object.keys(additionalTextLines).length > 0 && (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mt: 2,
                        mb: 1,
                        textAlign: 'center'
                    }}
                >
                    {additionalTextLines.map((line, index) => (
                        <span key={index}>
                            {line}
                            {index < additionalTextLines.length - 1 && <br />}
                        </span>
                    ))}
                </Typography>
            )}
        </Box>
    )
}

export default ApplicationLogo;